import React from 'react';
import { Plus } from '@phosphor-icons/react';
import { LiveSearch, Text, Button, Flex, TagBadge, type TagColor } from '@chatsdet/ui';
import { useEligibilityModel } from '../../../hooks/useEligibilityModel';

export type AddTestOption = { id: string; name: string; flowType?: string };
/** A tag plus the addable (not-yet-placed) runnable workflows that carry it. */
export type AddTagOption = { id: string; name: string; color: string; workflowIds: string[] };

/** Flow types that run automatically as part of another flow — not addable as standalone suite tests. */
export const AUTO_RUN_FLOW_TYPES = new Set(['setup', 'seed', 'teardown']);
const isAutoRun = (flowType?: string) => !!flowType && AUTO_RUN_FLOW_TYPES.has(flowType);

type Row =
  | { kind: 'tag'; key: string; tag: AddTagOption }
  | { kind: 'test'; key: string; test: AddTestOption };

interface AddTestsSearchProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Human label for where adds land (e.g. "Layer 2", "staging") — shapes copy. */
  targetLabel: string;
  /** Eligible tests not yet in the suite (shrinks as you add). */
  testOptions: AddTestOption[];
  /** Tags whose addable workflows can be bulk-added at once. */
  tagOptions: AddTagOption[];
  /** Add one or more workflows to the current target. */
  onAdd: (ids: string[]) => void;
}

/**
 * Add tests to a layer (or the staging rail) via the {@link LiveSearch} palette.
 * Surfaces both individual **tests** and **tags** — a tag row shows how many
 * addable flows carry it and bulk-adds them all. Live-filtered by test/tag name,
 * click/Enter to add, and the palette stays open so several can be added in a row.
 */
export function AddTestsSearch({
  isOpen,
  onOpenChange,
  targetLabel,
  testOptions,
  tagOptions,
  onAdd,
}: AddTestsSearchProps) {
  const [query, setQuery] = React.useState('');
  const [addedCount, setAddedCount] = React.useState(0);
  const runnableWord = useEligibilityModel() ? 'eligible' : 'promoted';

  React.useEffect(() => {
    if (isOpen) {
      setQuery('');
      setAddedCount(0);
    }
  }, [isOpen]);

  const rows = React.useMemo<Row[]>(() => {
    const q = query.trim().toLowerCase();
    // Tags first (bulk shortcuts), then individual tests. Tags with no addable
    // workflows are hidden — "add all" would be a no-op.
    const tags: Row[] = tagOptions
      .filter((t) => t.workflowIds.length > 0 && (!q || t.name.toLowerCase().includes(q)))
      .map((t) => ({ kind: 'tag', key: `tag:${t.id}`, tag: t }));
    const tests: Row[] = testOptions
      .filter((t) => !q || t.name.toLowerCase().includes(q))
      .map((t) => ({ kind: 'test', key: `test:${t.id}`, test: t }));
    return [...tags, ...tests];
  }, [query, testOptions, tagOptions]);

  const nothingAddable =
    testOptions.length === 0 && tagOptions.every((t) => t.workflowIds.length === 0);

  return (
    <LiveSearch<Row>
      isOpen={isOpen}
      onClose={() => onOpenChange(false)}
      query={query}
      onQueryChange={setQuery}
      items={rows}
      getItemKey={(r) => r.key}
      onSelect={(r) => {
        // Auto-run scripts (setup/seed/teardown) are shown for context but
        // can't be added — they run automatically when attached to a flow.
        if (r.kind === 'test' && isAutoRun(r.test.flowType)) return;
        const ids = r.kind === 'tag' ? r.tag.workflowIds : [r.test.id];
        if (ids.length === 0) return;
        onAdd(ids);
        setAddedCount((n) => n + ids.length);
      }}
      placeholder={`Search tests or tags to add to ${targetLabel}…`}
      aria-label={`Add tests to ${targetLabel}`}
      hint={
        <Text
          size="xs"
          weight="medium"
          className="px-3 pt-1 uppercase tracking-wider text-muted-foreground"
        >
          Quick add
        </Text>
      }
      emptyState={
        <div className="py-10 text-center text-sm text-muted-foreground">
          {nothingAddable
            ? `All ${runnableWord} tests are already in this suite.`
            : query.trim()
              ? `No tests or tags match “${query.trim()}”.`
              : 'No tests available.'}
        </div>
      }
      renderItem={(r, { active }) =>
        r.kind === 'tag' ? (
          <Flex align="center" justify="between" gap="sm" className="px-3 py-2.5">
            <Flex align="center" gap="sm" className="min-w-0">
              <TagBadge name={r.tag.name} color={r.tag.color as TagColor} size="sm" />
              <Text size="xs" className="shrink-0 text-muted-foreground">
                {r.tag.workflowIds.length} flow{r.tag.workflowIds.length === 1 ? '' : 's'}
              </Text>
            </Flex>
            <span
              className={`flex shrink-0 items-center gap-1 text-xs ${
                active ? 'text-foreground' : 'text-muted-foreground/0'
              }`}
            >
              <Plus size={14} /> Add all
            </span>
          </Flex>
        ) : isAutoRun(r.test.flowType) ? (
          <Flex align="center" justify="between" gap="sm" className="px-3 py-2.5 opacity-60">
            <Text size="sm" className="truncate text-muted-foreground">
              {r.test.name}
            </Text>
            <Text size="xs" className="shrink-0 italic text-muted-foreground">
              Runs automatically when attached to a flow
            </Text>
          </Flex>
        ) : (
          <Flex align="center" justify="between" gap="sm" className="px-3 py-2.5">
            <Text size="sm" className="truncate">
              {r.test.name}
            </Text>
            <span
              className={`flex shrink-0 items-center gap-1 text-xs ${
                active ? 'text-foreground' : 'text-muted-foreground/0'
              }`}
            >
              <Plus size={14} /> Add
            </span>
          </Flex>
        )
      }
      footer={
        <Flex align="center" justify="between" className="px-4 py-2.5">
          <Text size="xs" className="text-muted-foreground">
            {addedCount > 0
              ? `Added ${addedCount} — keep searching to add more.`
              : `${testOptions.length} test${testOptions.length === 1 ? '' : 's'} available`}
          </Text>
          <Button variant="secondary" size="sm" onPress={() => onOpenChange(false)}>
            Done
          </Button>
        </Flex>
      }
    />
  );
}
